<template>
  <div class="table-wrapper">
    <it-details v-if="getterPopupName === 'ImportItDetails'"
                :txnDetails="popupDetails" />
    <it-filter v-else-if="getterPopupName === 'ImportItFilter'"
                @updateFilters="updateFilters" :invalidTxns="true" />
    <it-export v-else-if="getterPopupName === 'ImportItExport'"
                :exportOptions="paginate" :exportRecords="exportRecords" />

    <div class="table-title">
      <div class="subject">
        <p>Invalid Entries</p>
        <span v-if="typeof paginate.total === 'number'">
          Total ({{paginate.total}})
        </span>
      </div>

      <div class="filters">
        <div class="options">
          <button @click="openPopup('ImportItExport')">
            Export
            <i class="fa fa-share" />
          </button>
          <button
            v-if="showFilters"
            @click="openPopup('ImportItFilter')"
            :class="{'filters-applied': filtersApplied}"
            class="dark-btn"
          >
            Filter
            <i class="fa fa-filter" />
          </button>
        </div>

        <it-search :placeholder="`Search ${searchPlaceholder}`"
                  @populateList="clearSearch" @setFunctions="setSearchFunctions"
                  :triggerSearch="triggerSearch" :searchTerm="searchTerm" />
      </div>
    </div>

    <div class="table-head" v-if="isDesktop()">
      <p
        v-for="(head, index) in colHeads"
        :key="index"
      >
        {{head}}
      </p>
    </div>

    <div class="table-body">
      <div class="table-row cursor"
        v-for="(data, index) in list"
        :key="index"
        @click="showDetails(data)"
      >
        <p
          v-for="(col, colIndex) in data.cols"
          :key="colIndex"
        >
          <b v-if="!isDesktop()">{{col.title}}:</b>
          <span>{{col.value}}</span>
        </p>
      </div>
    </div>

    <div class="table-empty" v-if="paginate.total === 0">
      <p v-if="searchTerm">Unable to find invalid entries with <span class="highlight">{{searchTerm}}</span>.<br>Please search with the complete {{searchPlaceholder}}.<br>Search is case sensitive - please search with the correct case to get the valid results.</p>
      <p v-else>{{emptyRecords}}</p>
    </div>

    <table-loader v-if="!paginate.data_added || list.length < paginate.total"
                  :css="{'single-side': !isDesktop()}" />
  </div>
</template>

<script>
import ItSearch from '@/components/global/SearchRecords'
import ItExport from '@/components/global/ExportConfirmation'
import ItFilter from '@/components/Imports/StatementFilter'
import ItDetails from '@/components/AccountStatement/StatementDetails'

import ApiServices from '@/services/api/imports'
import ListMixins from '@/mixins/listMixins'
import { amountFormat } from '@/utils/helpers'
import { mapGetters } from 'vuex'

export default {
  name: 'ImportInvalidTransactions',
  components: { ItSearch, ItExport, ItFilter, ItDetails },
  mixins: [ListMixins],
  data () {
    return {
      importId: null,
      addColsFunc: null,
      colHeads: [],
      searchPlaceholder: 'Ref number / Account number',
      showFilters: true
    }
  },
  created () {
    this.importId = this.$route.params.id
    this.startProcessing()
  },
  watch: {
    getterImportStatus () {
      this.resetData()
      this.populateData(0)
    },
    getterSectionDetails: {
      immediate: true,
      handler (newVal) {
        this.showFilters = newVal && newVal.txn_type === 'BANK_TRANSFERS'
      }
    }
  },
  computed: {
    ...mapGetters(['getterSectionDetails', 'getterImportStatus'])
  },
  methods: {
    amountFormat,
    populateParams () {
      this.api['search'] = ApiServices.searchInvalidTransactions
      this.api['export'] = ApiServices.exportInvalidTransactions
      this.keys['list'] = 'invalid_entries'
      this.keys['popup'] = 'ImportItDetails'
      const params = { import_id: this.importId }
      this.params['search'] = params
      this.params['export'] = params
      this.params['filtersLen'] = 0
      this.paginate['type'] = 'invalid entry'
    },
    fetchRecords (start) {
      return new Promise(resolve => {
        let params = {...this.filterOptions}
        params['import_id'] = this.importId
        if (start) {
          params['offset'] = start
          params['filter_token'] = this.paginate['filter_token']
        } else {
          params['fetch_import_details'] = !this.getterSectionDetails
        }

        ApiServices.invalidTransactions(params).then(response => {
          this.SET_STATEMENT_FILTER(this.filterOptions)
          let data = response.data
          if (data) {
            if (start) {
              data.invalid_entries = this.addColsFunc(data.invalid_entries)
              this.list.push(...data.invalid_entries)
            } else {
              this.setPaginate(data)
              this.populateMeta(response)
              this.list = this.addColsFunc(data.invalid_entries)
            }
            resolve()
          }
        }).catch((error) => {
          if (error.status === 404) {
            this.$router.push({name: 'ImportsList'})
          }
        })
      })
    },
    populateMeta ({data}) {
      if (data.import) {
        this.SET_SECTION_DETAILS(data.import)
      }

      if (this.getterSectionDetails.txn_type === 'BANK_TRANSFERS') {
        this.addColsFunc = this.bankTransferFunc
        this.colHeads = ['Amount', 'Payment Type', 'Account Number', 'IFSC', 'UPI ID', 'Beneficiary Name', 'Reference Number']
        this.searchPlaceholder = 'UPI ID / Ref number / Account number'
        this.setHighlightSelectors([3, 5, 7])
      } else {
        this.addColsFunc = this.bankVerifyFunc
        this.colHeads = ['Account Number', 'IFSC', 'Reference Number']
        this.searchPlaceholder = 'Ref number / Account number'
        this.setHighlightSelectors([1, 3])
      }
    },
    bankTransferFunc (list) {
      return list.map(e => {
        e.cols = [
          {
            title: 'Amount',
            value: this.amountFormat(e.amount)
          },
          {
            title: 'Pymt Type',
            value: e.payment_type
          },
          {
            title: 'Account Num',
            value: e.account_number
          },
          {
            title: 'IFSC',
            value: e.ifsc
          },
          {
            title: 'UPI ID',
            value: e.upi_id
          },
          {
            title: 'Beneficiary',
            value: e.beneficiary_name
          },
          {
            title: 'Ref Num',
            value: e.reference_number
          }
        ]
        return e
      })
    },
    bankVerifyFunc (list) {
      return list.map(e => {
        e.cols = [
          {
            title: 'Account Num',
            value: e.account_number
          },
          {
            title: 'IFSC',
            value: e.ifsc
          },
          {
            title: 'Ref Num',
            value: e.reference_number
          }
        ]
        return e
      })
    },
    showDetails (details) {
      this.popupDetails = details
      this.openPopup(this.keys.popup)
      let query = ''
      if (details.reference_number !== '-') {
        query = details.reference_number
      } else if (details.payment_type === 'UPI') {
        query = details.upi_id
      } else {
        query = details.account_number
      }
      this.$router.replace({query: {q: encodeURIComponent(query)}})
    },
    triggerSearch (initial) {
      this.searchTerm = this.getterSearchValue.trim()

      if (this.searchTerm) {
        this.resetData()
        const params = {
          ...this.params.search,
          list_meta_needed: initial,
          q: this.searchTerm
        }

        this.api.search(params).then((response) => {
          let data = response.data
          if (data) {
            if (initial) {
              this.populateMeta(response, true)
            }

            this.list = this.addColsFunc(data[this.keys.list]) || []
            this.paginate['total'] = this.list.length
            this.paginate['data_added'] = true
            this.paginate['filter_token'] = data.filter_token

            this.postSearch(initial, this.paginate.total)
          }
        }).catch(() => {
          this.list = []
          this.paginate['total'] = 0
          this.paginate['data_added'] = true
        })
      }
    }
  }
}
</script>

<style scoped lang="scss">
  @import '@/assets/scss/table.scss';

  .mobile, .tablet {
    .table-row {
      p {
        white-space: unset !important;

        &:nth-child(even) {
          text-align: right;
        }
      }
    }
  }
</style>
